import { trapiClient } from './trapiClient';
import type { Environment } from '../../contexts/AppContext';
import type { BackendCreateInput, BackendObject, BackendUpdateInput } from '../../types/trapi';

const RESERVED_BACKEND_UPDATE_FIELDS = [
  "id",
  "type",
  "ci",
  "name",
  "version",
  "health",
  "wholeBackendHealth",
  "healthControl",
  "healthAuthorityId",
] as const;

export function toBackendUpdatePayload(data: BackendUpdateInput): BackendUpdateInput {
  const raw = data as Record<string, unknown>;
  for (const field of RESERVED_BACKEND_UPDATE_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(raw, field)) {
      throw new Error(`Backend field '${field}' is immutable or server-managed.`);
    }
  }

  const payload: BackendUpdateInput = {};
  if (Object.prototype.hasOwnProperty.call(raw, 'url')) payload.url = data.url;
  if (Object.prototype.hasOwnProperty.call(raw, 'authType')) payload.authType = data.authType;
  if (Object.prototype.hasOwnProperty.call(raw, 'backendType')) payload.backendType = data.backendType;
  if (Object.prototype.hasOwnProperty.call(raw, 'keyVaultUrl')) payload.keyVaultUrl = data.keyVaultUrl;
  if (Object.prototype.hasOwnProperty.call(raw, 'properties')) payload.properties = data.properties;
  if (Object.prototype.hasOwnProperty.call(raw, 'ownership')) payload.ownership = data.ownership;
  return payload;
}

export class BackendsService {
  private static readonly BASE_PATH = '/configs';

  static async getBackends(params: { environment: Environment; accessToken: string; filter?: string }): Promise<BackendObject[]> {
    const { environment, accessToken, filter } = params;
    const queryParams = new URLSearchParams();
    queryParams.append('type', 'ADMINCI');
    queryParams.append('ci', 'BACKEND');
    if (filter && filter.includes('=')) queryParams.append('filter', filter);
    const url = `${this.BASE_PATH}?${queryParams.toString()}`;
    if (trapiClient.getEnvironment() !== environment) trapiClient.setEnvironment(environment);
    const response = await trapiClient.get<BackendObject[]>(url, accessToken);
    return Array.isArray(response) ? response : [];
  }

  static async getBackend(params: { id: string; environment: Environment; accessToken: string }): Promise<BackendObject> {
    const { id, environment, accessToken } = params;
    if (trapiClient.getEnvironment() !== environment) trapiClient.setEnvironment(environment);
    const url = `${this.BASE_PATH}/${id}`;
    return trapiClient.get<BackendObject>(url, accessToken);
  }

  static async createBackend(params: { data: BackendCreateInput; environment: Environment; accessToken: string }): Promise<BackendObject> {
    const { data, environment, accessToken } = params;
    if (trapiClient.getEnvironment() !== environment) trapiClient.setEnvironment(environment);
    const payload = { ...data, ci: 'BACKEND' };
    return trapiClient.put<BackendObject>(this.BASE_PATH, payload, accessToken);
  }

  static async updateBackend(params: { id: string; data: BackendUpdateInput; environment: Environment; accessToken: string }): Promise<BackendObject> {
    const { id, data, environment, accessToken } = params;
    if (trapiClient.getEnvironment() !== environment) trapiClient.setEnvironment(environment);
    const url = `${this.BASE_PATH}/${id}`;
    return trapiClient.put<BackendObject>(url, toBackendUpdatePayload(data), accessToken);
  }

  static async deleteBackend(params: { id: string; environment: Environment; accessToken: string }): Promise<void> {
    const { id, environment, accessToken } = params;
    if (trapiClient.getEnvironment() !== environment) trapiClient.setEnvironment(environment);
    return trapiClient.delete<void>(`${this.BASE_PATH}/${encodeURIComponent(id)}`, accessToken);
  }

  static async getBackendStats(environment: Environment, accessToken: string) {
    const backends = await this.getBackends({ environment, accessToken });
    const stats = {
      total: backends.length,
      byAuthType: backends.reduce((acc: Record<string, number>, b) => {
        const t = b.authType || 'Unknown';
        acc[t] = (acc[t] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      withKeyVault: backends.filter((b) => !!b.keyVaultUrl).length,
      healthy: 0,
      unhealthy: 0,
      unknown: backends.length,
    };
    return stats;
  }
}
